/**
 * Per-date price-override editor (DNKADM-0086 U2) — manages the `date-prices` rows
 * under ONE variation (DNKAPI-0168). It renders BESIDE {@link StaffPriceEditor} and
 * {@link WeeklyPriceEditor} in the variation's expand row (a sibling sub-section; the
 * other editors are unmoved).
 *
 * A list of date exceptions keyed by **`YYYY-MM-DD`** (the location-timezone calendar
 * date — the natural key, no surrogate ids). Each existing row shows its read-only date
 * with an editable price + Save/Remove; an "add" row carries a native
 * `<TextField type="date">` (the {@link HoursExceptionsEditor} convention — NO date-picker
 * dependency) + a price input. Add/edit upserts via `putDatePrice` (a duplicate date is an
 * upsert by contract — re-saving an existing date edits it); Remove drops the override via
 * `deleteDatePrice` (the date falls through to base). The list refetches via `getDatePrices`
 * after each write.
 *
 * Money-only — the variation's `creditCost` is unaffected (§Non-Goals). Client validation
 * mirrors the server (`>= 0`, ≤ 2 decimals); a `400` surfaces inline, anything else raises a
 * toast (the panel's error idiom, via `classifyFormError`). Precedence
 * (`Date > Staff > Day-of-week > Base`) is never resolved client-side.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import {
  deleteDatePrice,
  getDatePrices,
  putDatePrice,
  type DatePrice,
} from '@/api/services';
import { formatMoney } from '@/utils/v2Format';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';

export interface DatePriceEditorProps {
  serviceId: string;
  variationId: string;
  /** The variation's base price — shown as the "uses base" hint for unset dates. */
  basePrice: number;
  /** The currency code, or null while it loads. */
  currency: string | null;
  showToast: ShowToast;
  /** Fired after every successful price write (DNKADM-0304 U3) — see StaffPriceEditor. */
  onChanged?: () => void;
}

/**
 * Parse a price text field to a finite number ≥ 0 with ≤ 2 decimals (mirrors the
 * server), or null when invalid. A blank field is handled separately by the caller.
 */
function parsePrice(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function DatePriceEditor({
  serviceId,
  variationId,
  basePrice,
  currency,
  showToast,
  onChanged,
}: DatePriceEditorProps) {
  const [rows, setRows] = useState<DatePrice[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // The editable price text per existing date, seeded from the loaded overrides.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const [newDate, setNewDate] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await getDatePrices(serviceId, variationId);
        if (cancelled) return;
        setRows(loaded);
        setLoadError(false);
        const seeded: Record<string, string> = {};
        for (const r of loaded) seeded[r.date] = String(r.price);
        setDrafts(seeded);
        setRowError({});
      } catch {
        if (cancelled) return;
        setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId, variationId, reloadKey]);

  // The loaded rows, ordered by date so the list reads chronologically.
  const ordered = useMemo(() => [...rows].sort((a, b) => a.date.localeCompare(b.date)), [rows]);

  const basePlaceholder = currency ? formatMoney(basePrice, currency) : basePrice.toFixed(2);

  function setDraft(date: string, value: string) {
    setDrafts((prev) => ({ ...prev, [date]: value }));
  }

  async function handleSaveRow(date: string) {
    setRowError((prev) => ({ ...prev, [date]: '' }));
    const text = drafts[date] ?? '';
    const price = parsePrice(text);
    if (price === null) {
      setRowError((prev) => ({
        ...prev,
        [date]: 'Enter a valid price (0 or more, up to 2 decimals).',
      }));
      return;
    }
    setBusy(true);
    try {
      // Upsert by the natural date key — re-saving an existing date edits it.
      await putDatePrice(serviceId, variationId, date, price);
      setReloadKey((k) => k + 1);
      onChanged?.();
      showToast(`Price for ${date} saved`, 'success');
    } catch (err) {
      const view = classifyFormError(err, 'Couldn’t save the date price.');
      if (view.kind === 'inline') setRowError((prev) => ({ ...prev, [date]: view.message }));
      else showToast(view.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveRow(date: string) {
    setRowError((prev) => ({ ...prev, [date]: '' }));
    setBusy(true);
    try {
      await deleteDatePrice(serviceId, variationId, date);
      setReloadKey((k) => k + 1);
      onChanged?.();
      showToast(`Price for ${date} removed`, 'success');
    } catch (err) {
      const view = classifyFormError(err, 'Couldn’t remove the date price.');
      showToast(view.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleAdd() {
    setAddError(null);
    if (newDate.trim() === '') {
      setAddError('Choose a date.');
      return;
    }
    const price = parsePrice(newPrice);
    if (price === null) {
      setAddError('Enter a valid price (0 or more, up to 2 decimals).');
      return;
    }
    setBusy(true);
    try {
      // Upsert — adding a date that already has a row simply edits it (no id management).
      await putDatePrice(serviceId, variationId, newDate.trim(), price);
      setNewDate('');
      setNewPrice('');
      setReloadKey((k) => k + 1);
      onChanged?.();
      showToast(`Price for ${newDate.trim()} saved`, 'success');
    } catch (err) {
      const view = classifyFormError(err, 'Couldn’t save the date price.');
      if (view.kind === 'inline') setAddError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Date prices
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        Override this length’s price on specific dates (e.g. a holiday). A date with no
        override uses the base price ({basePlaceholder}); credits are unchanged.
      </Typography>

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 1, py: 0.5 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        >
          Couldn’t load the date prices.
        </Alert>
      )}

      {ordered.length > 0 && (
        <Table size="small" aria-label="Date prices" sx={{ mb: 1, maxWidth: 480 }}>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Save</TableCell>
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ordered.map((row) => {
              const error = rowError[row.date];
              return (
                <TableRow key={row.date}>
                  <TableCell>{row.date}</TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={drafts[row.date] ?? ''}
                      onChange={(e) => setDraft(row.date, e.target.value)}
                      disabled={busy}
                      error={Boolean(error)}
                      helperText={error || undefined}
                      sx={{ width: 150 }}
                      slotProps={{
                        htmlInput: {
                          'aria-label': `Price for ${row.date}`,
                          min: 0,
                          step: '0.01',
                          inputMode: 'decimal',
                        },
                        ...(currency
                          ? { input: { startAdornment: <InputAdornment position="start">{currency}</InputAdornment> } }
                          : {}),
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      aria-label={`Save price for ${row.date}`}
                      onClick={() => void handleSaveRow(row.date)}
                      disabled={busy}
                    >
                      Save
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <IconAction
                      size="small"
                      label={`Remove price for ${row.date}`}
                      onClick={() => void handleRemoveRow(row.date)}
                      disabled={busy}
                    >
                      <Icon name="delete" />
                    </IconAction>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        <TextField
          type="date"
          label="Date"
          size="small"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          disabled={busy}
          sx={{ width: 180 }}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { 'aria-label': 'New date override date' },
          }}
        />
        <TextField
          label="Price"
          type="number"
          value={newPrice}
          onChange={(e) => setNewPrice(e.target.value)}
          disabled={busy}
          size="small"
          slotProps={{
            htmlInput: { 'aria-label': 'New date override price', min: 0, step: '0.01', inputMode: 'decimal' },
            ...(currency ? { input: { startAdornment: <InputAdornment position="start">{currency}</InputAdornment> } } : {}),
          }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => void handleAdd()}
          disabled={busy}
          sx={{ mt: 0.5 }}
        >
          Add date price
        </Button>
      </Stack>

      {addError && (
        <Alert severity="error" role="alert" sx={{ mt: 1, py: 0.5 }}>
          {addError}
        </Alert>
      )}
    </Box>
  );
}
